import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { ActionTypes, AgGridColumnDef, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { AddressBookRecord, UserRecord } from '@tassat/fred-definitions';
import { AddressBookContact } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import { noop } from 'lodash';
import React from 'react';
import ReactGA from 'react-ga4';

import { submitAddCounterpartyForm } from 'Containers/client/forms/helpers/addCounterpartyFormFn';
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import Worker from 'Lib/worker/PrimusWorker';
import { ActionButtonsCellRenderer } from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';
import AddressBookWalletNameCellRenderer from 'SharedComponents/cellRenderComponent/AddressBookwalletNameCellRenderer';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import IsFavoriteCellRenderer from 'SharedComponents/cellRenderComponent/isFavoriteCellRenderer';
import WalletAddressCellRenderer from 'SharedComponents/cellRenderComponent/walletAddressCellRenderer';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import { AddressBookGridOptions, getAddressBookColumnDefs } from './ABookGridOptions';

import './ABookGridStyles.css';

type RowGroupState = {
    groupId: string;
    first: AddressBookRecord[];
    others: AddressBookRecord[];
};

type ABookAgGridProps = {
    contacts: RowGroupState;
    user: UserRecord;
    editContact: any;
    areRecordsExpanded: boolean;
    rowGroupState: Record<string, boolean>;
};

const AddressBookAgGrid: React.FC<ABookAgGridProps> = ({
    contacts,
    editContact,
    user,
    rowGroupState
}) => {
    const [features] = useFeatures();
    const [ _, onGridReady ] = useAgGrid();
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const deleteAddressBookContactValidation = (aBookContact: AddressBookContact): boolean => {
        const wallet = aBookContact;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: aBookContact,
                actionType: ActionTypes.delete,
                confirmTitle: `Confirm Deleting Account for ${wallet.FirmName}`,
                confirmBody: `Please confirm deleting the Account for ${wallet.FirmName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const deleteAddressBookContact = async (aBookContact: AddressBookContact) => {
        const id = { AddressBookId: aBookContact.AddressBookId };
        const wallet = aBookContact;

        try {
            const response = await Worker.getInstance()
                .deleteAddressBookRecord(id);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Account for ${wallet.FirmName}`,
                        successBody: `Successfully deleted the Account for ${wallet.FirmName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Account for ${wallet.FirmName}`,
                        failureBody: `Could not delete the Account for ${wallet.FirmName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Account for ${wallet.FirmName}`,
                    failureBody: `Something went wrong deleting the Account for ${wallet.FirmName}`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                preActionValidationAndConfig: {
                    [ActionTypes.delete]: deleteAddressBookContactValidation,
                    [ActionTypes.edit]: editContact
                },
                onConfirm: { [ActionTypes.delete]: deleteAddressBookContact },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    const handleContactFavoriteChange = React.useCallback((data: AddressBookContact): void => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'address_book_toggle_favorite'
        });
        submitAddCounterpartyForm({
            nickName1: data.NickName,
            nickName2: '',
            Wallet: data.Account,
            IsFavorite: !data.IsFavorite,
            AssociatedAccountNumber: data.AssociatedAccountNumber
        }, {
            actionType: ActionTypes.edit,
            user,
            isFavorited: !data.IsFavorite,
            contactForEdit: data,
            onClose: noop,
            setSubmitError: null
        });
    }, [user]);

    const AddressBookColumnDefs = React.useMemo((): AgGridColumnDef[] => {
        return getAddressBookColumnDefs(features, handleContactFavoriteChange);
    }, [ features, handleContactFavoriteChange ]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <div
                className='ag-theme-material'
                id='addressBookGrid'
                style={{
                    height: '100%',
                    width: '100%'
                }}
            >
                <AgGridReact
                    animateRows={false}
                    columnDefs={AddressBookColumnDefs}
                    columnMenu='legacy'
                    components={{
                        dateCellRenderer: DateCellRender,
                        walletNameCellRenderer: AddressBookWalletNameCellRenderer,
                        walletAddressCellRenderer: WalletAddressCellRenderer,
                        actionButtonsCellRenderer: ActionButtonsCellRenderer,
                        isFavoriteCellRenderer: IsFavoriteCellRenderer
                    }}
                    defaultColDef={{
                        resizable: true,
                        filter: false,
                        sortable: false
                    }}
                    enableCellTextSelection
                    gridOptions={AddressBookGridOptions}
                    headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                    onGridReady={onGridReady}
                    rowData={rowGroupState[contacts.groupId] ? [ ...contacts.first, ...contacts.others ] : [...contacts.first]}
                    suppressAnimationFrame
                    suppressCellFocus
                    suppressContextMenu
                    suppressRowTransform
                    suppressScrollOnNewData
                />
            </div>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
};

export default AddressBookAgGrid;